import { query, writeQuery, waitForAllReturnWhenError } from '@confluence/query-preloader-tools';
import { SYSTEM_CONTENT_BYLINE } from '@confluence/web-item-location/entry-points/WEB_FRAGMENT_LOCATIONS';
import { preloadWebItemLocation } from '@confluence/web-item-location/entry-points/preloadWebItemLocation';
import { preloadCollabDraftContent } from '@confluence/load-edit-page/entry-points/preloadNativeEditor';
import { FORGE_MODULE_BYLINE } from '@confluence/forge-ui/entry-points/ForgeModuleType';
import { preloadUseExtensionList } from '@confluence/forge-ui/entry-points/preloadUseExtensionList';
import { preloadMentionsProvider } from '@confluence/fabric-media-support/entry-points/preloadMentions';
import { preloadReadTime } from '@confluence/read-time/entry-points/preloadReadTime';
import { fg } from '@confluence/feature-gating';
import { NativeCollabQuery } from '@confluence/native-collab/entry-points/NativeCollabQuery';

import { LivePageUnifiedQuery } from './LivePageUnifiedQuery.graphql';
import type {
	LivePageUnifiedQuery as LivePageUnifiedQueryType,
	LivePageUnifiedQueryVariables,
} from './__types__/LivePageUnifiedQuery';

export type PreloadLivePageDataParams = {
	userId: string | null;
	contentId: string;
	spaceKey: string;
};

/**
 * We combine what we can into a single unified query.
 * More complex preloaders will be split out for now.
 */
export function preloadLivePageData({ userId, contentId, spaceKey }: PreloadLivePageDataParams) {
	const preloaders: Array<Promise<any>> = [];

	// 1. Editor data

	// specific native collab editor queries
	if (!!contentId) {
		preloaders.push(preloadCollabDraftContent(contentId, '', false));
		preloaders.push(preloadMentionsProvider());
	}

	preloaders.push(
		// Pre-loading apps in the byline area for live pages. Currently only live pages have extension point apps and not classic editor.
		preloadWebItemLocation({ contentId, location: SYSTEM_CONTENT_BYLINE }),
		// For some reason the addition of the filter (uses new Forge query) is causing SSR to timeout - workaround by skipping the filter
		preloadUseExtensionList({
			spaceKey: spaceKey || '',
			moduleType: FORGE_MODULE_BYLINE,
			skipFilter: true,
		}),
	);

	preloaders.push(preloadReadTime({ contentId, useCache: false }));

	// 2. Unified data
	return waitForAllReturnWhenError([
		query<LivePageUnifiedQueryType, LivePageUnifiedQueryVariables>({
			query: LivePageUnifiedQuery,
			variables: {
				userId,
				contentId,
				spaceKey,
				isUsdSsr: fg('confluence_frontend_usd_ssr'),
				shouldIncludePermissionType: fg('confluence_team_presence_general_availability'),
			},
			fetchPolicy: 'network-only',
		}).then((response) => {
			if (response.data) {
				const nativeCollabVariables = {
					contentId,
					status: ['draft', 'current'],
				};

				writeQuery({
					query: NativeCollabQuery,
					variables: nativeCollabVariables,
					data: response.data,
				});
			}
		}),
		...preloaders,
	]);
}
